import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Truck, Eye, EyeOff, Mail, Lock, User, Phone, 
  ArrowLeft, Car, Bike, Package, BadgeCheck, Building2,
  Upload, FileCheck, CreditCard, IdCard, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeliveryAuthPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingLicense, setUploadingLicense] = useState(false);
  
  const idCardInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    name: '', phone: '', email: '', password: '', 
    driver_type: '', vehicle_type: '', license_plate: '',
    id_card_photo: null, driver_license_photo: null
  });

  const handleFileUpload = async (file, type) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      if (type === 'id_card') setUploadingId(true);
      else setUploadingLicense(true);
      
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Documento carregado');
      return res.data.url;
    } catch (err) {
      toast.error('Falha ao carregar documento');
      return null;
    } finally {
      if (type === 'id_card') setUploadingId(false);
      else setUploadingLicense(false);
    }
  };

  const handleIdCardChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await handleFileUpload(file, 'id_card');
      if (url) setRegisterData({ ...registerData, id_card_photo: url });
    }
  };

  const handleLicenseChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = await handleFileUpload(file, 'license');
      if (url) setRegisterData({ ...registerData, driver_license_photo: url });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/driver/login`, loginData);
      localStorage.setItem('halubri_driver_token', res.data.access_token);
      toast.success('Bem-vindo de volta!');
      navigate('/delivery/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciais inválidas');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.driver_type) {
      toast.error('Seleccione o tipo de entregador');
      return;
    }
    if (!registerData.vehicle_type) {
      toast.error('Seleccione o tipo de veículo');
      return;
    }
    if (!registerData.id_card_photo) {
      toast.error('Carregue a foto do seu Bilhete de Identidade');
      return;
    }
    if (!registerData.driver_license_photo) {
      toast.error('Carregue a foto da sua Carta de Condução');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/driver/register`, registerData);
      setRegistrationSuccess(res.data.driver_id);
      toast.success('Registo submetido!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    }
    setLoading(false);
  };

  // Success screen after registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-4">
        <div className="bg-zinc-800 rounded-2xl p-8 max-w-md w-full text-center border border-zinc-700">
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <BadgeCheck className="w-10 h-10 text-yellow-500" />
          </div>
          <h2 className="font-barlow text-2xl font-bold text-white mb-2">
            Registo Submetido!
          </h2>
          <p className="text-zinc-400 mb-6">
            O seu registo foi submetido para verificação. Receberá confirmação em breve.
          </p>
          
          <div className="bg-zinc-900 rounded-xl p-4 mb-4">
            <p className="text-zinc-500 text-sm mb-1">O seu ID de Entregador</p>
            <p className="font-barlow text-2xl font-bold text-sonangol-orange tracking-wider">
              {registrationSuccess}
            </p>
          </div>
          
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
            <p className="text-green-400 text-sm">
              <strong>Para iniciar sessão:</strong> Utilize o seu <strong>email</strong> e palavra-passe após a verificação da conta.
            </p>
          </div>
          
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
            <p className="text-yellow-400 text-sm">
              <strong>Aguarde Verificação:</strong> A sua conta será activada após validação dos dados.
            </p>
          </div>
          
          <Button 
            className="w-full h-12 bg-zinc-700 hover:bg-zinc-600"
            onClick={() => navigate('/auth')}
          >
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col">
      {/* Header */}
      <div className="bg-zinc-800 pt-6 pb-8 px-4 border-b border-zinc-700">
        <div className="flex items-center gap-4 mb-4">
          <Link to="/auth" className="tap-target text-zinc-400 hover:text-white" data-testid="auth-back-link">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <span className="text-zinc-500 text-sm">Voltar</span>
        </div>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg mb-4">
            <Truck className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-barlow text-2xl font-bold text-white uppercase tracking-tight">
            Portal Entregador
          </h1>
          <p className="text-zinc-400 text-sm mt-1 font-manrope">SONANGOL · Entregas HáLubri</p>
        </div>
      </div>

      {/* Auth form */}
      <div className="flex-1 px-4 py-6">
        <div className="bg-zinc-800 rounded-2xl p-6 max-w-md mx-auto border border-zinc-700">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-zinc-700">
              <TabsTrigger value="login" className="data-[state=active]:bg-green-600" data-testid="driver-login-tab">
                Entrar
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-green-600" data-testid="driver-register-tab">
                Registar
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                {/* Info box */}
                <div className="bg-zinc-700/50 rounded-lg p-3 mb-2">
                  <p className="text-zinc-400 text-xs">
                    Utilize o <strong className="text-white">email</strong> que registou para iniciar sessão.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="driver-email" className="text-zinc-300">Email de Registo</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="driver-email"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="driver-login-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="driver-login-password" className="text-zinc-300">Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="driver-login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 bg-zinc-900 border-zinc-600 text-white"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="driver-login-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-green-600 hover:bg-green-700 font-barlow font-bold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="driver-login-submit-btn"
                >
                  {loading ? 'A entrar...' : 'Entrar'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Driver Type Selection */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Tipo de Entregador</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setRegisterData({ ...registerData, driver_type: 'individual' })}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        registerData.driver_type === 'individual' 
                          ? 'border-green-500 bg-green-500/10' 
                          : 'border-zinc-600 hover:border-zinc-500'
                      }`}
                    >
                      <Bike className={`w-6 h-6 mx-auto mb-1 ${registerData.driver_type === 'individual' ? 'text-green-500' : 'text-zinc-400'}`} />
                      <p className={`text-sm font-medium ${registerData.driver_type === 'individual' ? 'text-green-500' : 'text-zinc-300'}`}>Individual</p>
                      <p className="text-xs text-zinc-500">Pequenas entregas</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegisterData({ ...registerData, driver_type: 'corporate' })}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        registerData.driver_type === 'corporate' 
                          ? 'border-sonangol-orange bg-sonangol-orange/10' 
                          : 'border-zinc-600 hover:border-zinc-500'
                      }`}
                    >
                      <Truck className={`w-6 h-6 mx-auto mb-1 ${registerData.driver_type === 'corporate' ? 'text-sonangol-orange' : 'text-zinc-400'}`} />
                      <p className={`text-sm font-medium ${registerData.driver_type === 'corporate' ? 'text-sonangol-orange' : 'text-zinc-300'}`}>Corporate</p>
                      <p className="text-xs text-zinc-500">Grandes volumes</p>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="driver-name" className="text-zinc-300">Nome Completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="driver-name"
                      type="text"
                      placeholder="João Silva"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                      data-testid="driver-register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="driver-phone" className="text-zinc-300">Telefone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="driver-phone"
                      type="tel"
                      placeholder="+244 923 456 789"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      required
                      data-testid="driver-register-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="driver-email-reg" className="text-zinc-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="driver-email-reg"
                      type="email"
                      placeholder="seu@email.com"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      data-testid="driver-register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Tipo de Veículo</Label>
                  <Select 
                    value={registerData.vehicle_type} 
                    onValueChange={(value) => setRegisterData({ ...registerData, vehicle_type: value })}
                  >
                    <SelectTrigger className="h-12 bg-zinc-900 border-zinc-600 text-white" data-testid="driver-vehicle-select">
                      <Car className="w-5 h-5 text-zinc-500 mr-2" />
                      <SelectValue placeholder="Seleccione o veículo" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="moto" className="text-white hover:bg-zinc-700">Motocicleta</SelectItem>
                      <SelectItem value="car" className="text-white hover:bg-zinc-700">Carro</SelectItem>
                      <SelectItem value="van" className="text-white hover:bg-zinc-700">Van / Carrinha</SelectItem>
                      <SelectItem value="truck" className="text-white hover:bg-zinc-700">Camião</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="driver-plate" className="text-zinc-300">Matrícula (opcional)</Label>
                  <div className="relative">
                    <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="driver-plate"
                      type="text"
                      placeholder="LD-00-00-AA"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={registerData.license_plate}
                      onChange={(e) => setRegisterData({ ...registerData, license_plate: e.target.value })}
                      data-testid="driver-register-plate-input"
                    />
                  </div>
                </div>

                {/* Document Uploads */}
                <div className="bg-zinc-700/30 rounded-xl p-4 space-y-4">
                  <p className="text-zinc-300 text-sm font-medium flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-green-500" />
                    Documentos Obrigatórios
                  </p>
                  
                  {/* ID Card Upload */}
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm">Bilhete de Identidade *</Label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      ref={idCardInputRef}
                      onChange={handleIdCardChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => idCardInputRef.current?.click()}
                      disabled={uploadingId}
                      className={`w-full p-4 rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-3 ${
                        registerData.id_card_photo 
                          ? 'border-green-500 bg-green-500/10' 
                          : 'border-zinc-600 hover:border-zinc-500 bg-zinc-900'
                      }`}
                    >
                      {uploadingId ? (
                        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                      ) : registerData.id_card_photo ? (
                        <>
                          <FileCheck className="w-5 h-5 text-green-500" />
                          <span className="text-green-500 text-sm">BI Carregado</span>
                        </>
                      ) : (
                        <>
                          <IdCard className="w-5 h-5 text-zinc-400" />
                          <span className="text-zinc-400 text-sm">Carregar foto do BI</span>
                        </>
                      )}
                    </button>
                  </div>
                  
                  {/* Driver's License Upload */}
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm">Carta de Condução *</Label>
                    <input 
                      type="file" 
                      accept="image/*" 
                      ref={licenseInputRef}
                      onChange={handleLicenseChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => licenseInputRef.current?.click()}
                      disabled={uploadingLicense}
                      className={`w-full p-4 rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-3 ${
                        registerData.driver_license_photo 
                          ? 'border-green-500 bg-green-500/10' 
                          : 'border-zinc-600 hover:border-zinc-500 bg-zinc-900'
                      }`}
                    >
                      {uploadingLicense ? (
                        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                      ) : registerData.driver_license_photo ? (
                        <>
                          <FileCheck className="w-5 h-5 text-green-500" />
                          <span className="text-green-500 text-sm">Carta Carregada</span>
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 text-zinc-400" />
                          <span className="text-zinc-400 text-sm">Carregar foto da Carta</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="driver-register-password" className="text-zinc-300">Criar Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="driver-register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 bg-zinc-900 border-zinc-600 text-white"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="driver-register-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-green-600 hover:bg-green-700 font-barlow font-bold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="driver-register-submit-btn"
                >
                  {loading ? 'A submeter...' : 'Submeter Registo'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center text-zinc-600 text-xs font-manrope">
        © 2025 Sonangol E.P. · Portal de Entregadores
      </div>
    </div>
  );
};

export default DeliveryAuthPage;
